import {
  type ChallengeAPIResponse,
  type Claim,
  getMemberDetailsFromUserDetails,
  type RepliesResponse,
  type UserDetails,
} from '@assembly-web/services';
import {
  ChallengeCard,
  IconButton,
  TextStyle,
  Toolbar,
  type ToolbarItem,
  useToolbarState,
} from '@assembly-web/ui';
import { ChevronDownIcon, LinkIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { isUserAdmin } from '../../../../../services/workspace';
import { ToolbarItemId } from '../../cards/utils';

type ChallengeContainerProps = {
  userDetails: UserDetails;
  repliesData: RepliesResponse;
  challengeDetails: ChallengeAPIResponse;
};

const messages = defineMessages({
  originalPost: {
    defaultMessage: 'Original post',
    id: 'p1pzyX',
  },
  launched: {
    defaultMessage: `Launched {timeAgo}`,
    id: 'EmKQ2d',
  },
  copyLink: {
    defaultMessage: 'Copy link',
    id: 'lbr3Lq',
  },
});

export function ChallengeContainer({
  userDetails,
  repliesData,
  challengeDetails,
}: ChallengeContainerProps) {
  const {
    challengeId,
    claimButtonText,
    description,
    title,
    points,
    state,
    type,
    createdBy,
    canEdit,
    claimsRemaining,
    reactions,
    selfClaimDetails,
    interactionSettings,
    launchedAt,
  } = challengeDetails;

  const { formatMessage } = useIntl();
  const { getToolbarProps } = useToolbarState({ hideToolbar: false });

  const [isPostExpanded, setPostExpanded] = useState(true);

  const {
    assembly: { currency },
    member: { role },
  } = userDetails;

  const descriptionText =
    typeof description === 'undefined' ? '' : description.messageHtml;

  const { hideReactions = false, hideReplies = false } =
    interactionSettings ?? {};

  const onMenuItemClick = () => {};

  const secondaryToolbarItems: ToolbarItem[] = [
    {
      id: ToolbarItemId.CopyLink,
      icon: LinkIcon,
      text: formatMessage(messages.copyLink),
    },
  ];

  return (
    <section className="px-6 py-4">
      <div className="grid w-full grid-cols-[1fr_auto] justify-between">
        <IconButton
          variation="tertiaryLite"
          className="-mt-1 w-fit"
          onClick={() => {
            setPostExpanded(!isPostExpanded);
          }}
          size="small"
        >
          <TextStyle variant="sm-regular" className="text-gray-8">
            {formatMessage(messages.originalPost)}
          </TextStyle>
          <ChevronDownIcon
            className={twMerge(
              'h-4 w-4 text-gray-8',
              isPostExpanded && 'rotate-180'
            )}
          />
        </IconButton>
        <div className="flex gap-2">
          <TextStyle variant="sm-regular" className="text-gray-8">
            {formatMessage(messages.launched, {
              timeAgo: dayjs(launchedAt).fromNow(),
            })}
          </TextStyle>
          <Toolbar
            {...getToolbarProps({ onMenuItemClick })}
            secondaryToolbarItems={secondaryToolbarItems}
            compact
            hideWhenDetached
          />
        </div>
      </div>
      <AnimatePresence initial={false}>
        <motion.div
          animate={isPostExpanded ? 'open' : 'collapsed'}
          variants={{
            open: { opacity: 1, height: 'auto', transition: { duration: 0.3 } },
            collapsed: { opacity: 0, height: 0, transition: { duration: 0.3 } },
          }}
          initial="collapsed"
          exit="collapsed"
          className="flex w-full flex-col py-2"
        >
          <ChallengeCard
            type={type}
            title={title}
            state={state}
            points={points}
            canEdit={canEdit}
            currency={currency}
            reactions={reactions}
            createdBy={createdBy}
            launchDate={launchedAt}
            hideReplies={hideReplies}
            challengeId={challengeId}
            repliesData={repliesData}
            hideReactions={hideReactions}
            description={descriptionText}
            claimsRemaining={claimsRemaining}
            claimButtonText={claimButtonText}
            selfClaimDetails={
              selfClaimDetails ?? {
                totalCount: 0,
                totalEarnedPoints: 0,
                claims: [] as Claim[],
              }
            }
            isUserAdmin={isUserAdmin({ role })}
            currentUserDetails={getMemberDetailsFromUserDetails(
              userDetails.member
            )}
          />
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
